import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FileText, GripVertical, BarChart3, User, Mail, Phone, Edit2 } from 'lucide-react';
import { getMonthOptions, getModuleWorkstreams, getWorkstreamByKey } from '../../config/moduleHelpers';
import { formatDate, formatRelativeTime, parseDateInput, Badge } from '../ui/UIComponents';
import ExternalQuickLinksButton from '../ui/ExternalQuickLinksButton';

// Helper component for auto-opening dropdown
const StatusDropdown = ({ value, options, onChange, onCancel }) => {
    const selectRef = useRef(null);
    
    useEffect(() => {
        // Auto-open dropdown on mount
        if (selectRef.current) {
            selectRef.current.focus();
            // Use setTimeout to ensure the dropdown opens after focus
            setTimeout(() => {
                if (selectRef.current && selectRef.current.showPicker) {
                    selectRef.current.showPicker();
                } else if (selectRef.current) {
                    // Fallback: trigger click for browsers that don't support showPicker
                    const event = new MouseEvent('mousedown', { bubbles: true });
                    selectRef.current.dispatchEvent(event);
                }
            }, 0);
        }
    }, []);
    
    return (
        <div className="relative">
            <select
                ref={selectRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onCancel}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') onCancel();
                }}
                className="appearance-none bg-white border border-slate-200 hover:border-blue-300 rounded px-2 py-1 pr-6 w-full text-xs cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
            >
                <option value="" disabled>Set Status...</option>
                {options.map(s => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>
        </div>
    );
};

// Helper component for auto-opening staff dropdown
const StaffDropdown = ({ value, options, onChange, onCancel }) => {
    const selectRef = useRef(null);
    
    useEffect(() => {
        // Auto-open dropdown on mount
        if (selectRef.current) {
            selectRef.current.focus();
            // Use setTimeout to ensure the dropdown opens after focus
            setTimeout(() => {
                if (selectRef.current && selectRef.current.showPicker) {
                    selectRef.current.showPicker();
                } else if (selectRef.current) {
                    // Fallback: trigger click for browsers that don't support showPicker
                    const event = new MouseEvent('mousedown', { bubbles: true });
                    selectRef.current.dispatchEvent(event);
                }
            }, 0);
        }
    }, []);
    
    return (
        <div className="relative">
            <select
                ref={selectRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onCancel}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') onCancel();
                }}
                className="appearance-none bg-white border border-slate-200 hover:border-blue-300 rounded px-2 py-1 pr-6 w-full text-xs cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
            >
                <option value="">Unassigned</option>
                {options.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                ))}
            </select>
        </div>
    );
};

const ClientKanbanView = ({ 
    data, 
    config,
    plannerType = 'acc', // 'acc', 'tax', or 'cs'
    onMoveMonth,
    onBulkMove,
    selectedClients,
    onToggleSelection,
    onClientClick,
    staffList,
    workflows = {},
    onSaveNotes,
    onUpdate,
    onOpenEmail
}) => {
    const [draggedClientId, setDraggedClientId] = useState(null);
    const [dragOverMonth, setDragOverMonth] = useState(null);
    const [showCapacityFor, setShowCapacityFor] = useState(null);
    const [editingField, setEditingField] = useState(null); // { clientId, field }
    const [editingValue, setEditingValue] = useState('');
    const [assignmentFilter, setAssignmentFilter] = useState(null); // null, 'assigned', or 'unassigned'
    const workstreams = useMemo(() => getModuleWorkstreams(config), [config]);
    const activeWorkstream = useMemo(
        () => getWorkstreamByKey(config, plannerType) || workstreams[0] || {},
        [config, plannerType, workstreams]
    );

    // Generate month options (current month + 23 future months)
    const planningMonths = useMemo(() => getMonthOptions(24), []);

    // Group clients by month
    const clientsByMonth = useMemo(() => {
        const groups = {};
        planningMonths.forEach(month => { groups[month] = []; });
        groups['Unscheduled'] = [];
        
        const plannedMonthField = activeWorkstream?.planField;
        
        data.forEach(client => {
            const month = plannedMonthField ? (client[plannedMonthField] || 'Unscheduled') : 'Unscheduled';
            if (groups[month]) {
                groups[month].push(client);
            } else {
                groups['Unscheduled'].push(client);
            }
        });
        
        return groups;
    }, [activeWorkstream, data, planningMonths]);

    // Capacity stats helper
    const getCapacityStats = (clientsInMonth) => {
        const totalJobs = clientsInMonth.length;
        const assigned = clientsInMonth.filter(client => activeWorkstream?.staffField && client[activeWorkstream.staffField]).length;
        const capacity = totalJobs > 0 ? Math.round((assigned / totalJobs) * 100) : 0;
        return { totalJobs, assigned, unassigned: totalJobs - assigned, capacity };
    };

    // Drag handlers
    const handleDragStart = (e, clientId) => {
        setDraggedClientId(clientId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, month) => {
        e.preventDefault();
        setDragOverMonth(month);
    };

    const handleDrop = async (e, targetMonth) => {
        e.preventDefault();
        if (draggedClientId && onMoveMonth) {
            await onMoveMonth(draggedClientId, targetMonth);
        }
        setDraggedClientId(null);
        setDragOverMonth(null);
    };

    // Extract year from a date string (for CS REF)
    const getYearFromDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).getFullYear().toString();
    };

    // Calculate due date as NEXT + 1 year
    const calculateDueDate = (nextDate) => {
        if (!nextDate) return null;
        const next = new Date(nextDate);
        next.setFullYear(next.getFullYear() + 1);
        return next.toISOString().split('T')[0];
    };
    const getUrgencyStyle = (dueDate) => {
        if (!dueDate) return { border: 'border-slate-300', bg: 'bg-white', accentColor: 'text-slate-500' };
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = parseDateInput(dueDate);
        if (!due) return { border: 'border-slate-300', bg: 'bg-white', accentColor: 'text-slate-500' };
        due.setHours(0, 0, 0, 0);
        const daysUntil = Math.round((due - today) / (1000 * 60 * 60 * 24));
        
        if (daysUntil < 0) return { border: 'border-red-400', bg: 'bg-red-50', accentColor: 'text-red-700', shadow: 'shadow-md' };
        if (daysUntil <= 7) return { border: 'border-orange-400', bg: 'bg-orange-50', accentColor: 'text-orange-700', shadow: 'shadow-md' };
        if (daysUntil <= 14) return { border: 'border-amber-400', bg: 'bg-amber-50', accentColor: 'text-amber-700' };
        if (daysUntil <= 21) return { border: 'border-yellow-300', bg: 'bg-yellow-50', accentColor: 'text-yellow-700' };
        if (daysUntil <= 30) return { border: 'border-lime-300', bg: 'bg-lime-50', accentColor: 'text-lime-700' };
        return { border: 'border-green-300', bg: 'bg-green-50', accentColor: 'text-green-700' };
    };

    // Format date for display
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '-';
        return formatDate(dateString);
    };

    // Format short date (DD/MM)
    const formatShortDate = (dateString) => {
        if (!dateString) return '-';
        const date = parseDateInput(dateString);
        if (!date) return '-';
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
    };

    // Get time until due date
    const getTimeUntilDue = (dateString) => {
        if (!dateString) return null;
        const relative = formatRelativeTime(dateString);
        const bgColor = relative.color
            .split(' ')
            .find(token => token.startsWith('bg-')) || 'bg-slate-500';

        return { text: relative.text, color: bgColor };
    };

    // Client Card Component
    const ClientCard = ({ client }) => {
        const isSelected = selectedClients?.has(client.id);
        const [editingLocalField, setEditingLocalField] = useState(null);
        const [editingLocalValue, setEditingLocalValue] = useState('');

        // Get work-specific information based on plannerType
        const getWorkInfo = () => ({
            dueDate: activeWorkstream?.dueField ? client[activeWorkstream.dueField] : null,
            refDate: activeWorkstream?.refField ? client[activeWorkstream.refField] : null,
            nextDate: activeWorkstream?.nextField ? client[activeWorkstream.nextField] : null,
            status: activeWorkstream?.statusField ? client[activeWorkstream.statusField] : '',
            statusField: activeWorkstream?.statusField,
            assignedStaff: activeWorkstream?.staffField ? client[activeWorkstream.staffField] : '',
            staffField: activeWorkstream?.staffField,
            label: (activeWorkstream?.label || 'Workflow').toUpperCase(),
            colorClass: activeWorkstream?.textColorClass || 'text-slate-700'
        });

        const workInfo = getWorkInfo();
        const assignedStaffName = staffList?.find(s => s.id === workInfo.assignedStaff)?.name;
        const timeUntilDue = getTimeUntilDue(workInfo.dueDate);
        const urgencyStyle = getUrgencyStyle(workInfo.dueDate);

        const startEditing = (field, currentValue) => {
            setEditingLocalField(field);
            setEditingLocalValue(currentValue || '');
        };

        const saveEdit = async () => {
            if (editingLocalField && onUpdate) {
                await onUpdate(client.id, editingLocalField, editingLocalValue);
            }
            setEditingLocalField(null);
            setEditingLocalValue('');
        };

        const cancelEdit = () => {
            setEditingLocalField(null);
            setEditingLocalValue('');
        };

        return (
            <div
                draggable
                onDragStart={(e) => handleDragStart(e, client.id)}
                className={`${urgencyStyle.bg} rounded-lg border-2 ${urgencyStyle.border} shadow-sm mb-3 cursor-move hover:${urgencyStyle.shadow} transition-all overflow-hidden ${
                    isSelected ? 'ring-2 ring-purple-400 border-purple-400' : ''
                } ${draggedClientId === client.id ? 'opacity-50' : ''}`}
            >
                {/* Header with grip and checkbox stacked, company name on right */}
                <div className="flex items-start gap-3 px-4 pt-4 pb-3 bg-gradient-to-r from-slate-50 to-transparent">
                    <div className="flex flex-col items-center gap-1">
                        <GripVertical className={`w-4 h-4 flex-shrink-0 ${urgencyStyle.accentColor}`} />
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleSelection && onToggleSelection(client.id);
                            }}
                            className={`w-5 h-5 rounded border-2 flex-shrink-0 transition-colors ${
                                isSelected ? 'bg-purple-500 border-purple-500' : 'border-slate-300 hover:border-purple-400'
                            }`}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-slate-900 leading-tight break-words">{client.name}</h3>
                        <div className={`text-xs font-semibold ${workInfo.colorClass} mt-0.5`}>{workInfo.label}</div>
                    </div>
                    <ExternalQuickLinksButton
                        config={config}
                        record={client}
                        compact
                        className="flex-shrink-0"
                    />
                </div>

                {/* REF / NEXT / DUE Row */}
                <div className="px-4 py-3 border-t border-slate-200/50">
                    <div className="grid gap-4 text-xs" style={{ gridTemplateColumns: '1fr 1fr 1.2fr' }}>
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-1">REF</div>
                            <div className={`text-sm font-semibold ${urgencyStyle.accentColor}`}>{workInfo.refDate || '-'}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-1">NEXT</div>
                            <div className={`text-sm font-semibold ${urgencyStyle.accentColor}`}>{formatDateDisplay(workInfo.nextDate)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-1">DUE</div>
                            <div className={`text-sm font-semibold ${urgencyStyle.accentColor}`}>{formatDateDisplay(workInfo.dueDate)}</div>
                        </div>
                    </div>
                </div>

                {/* Due pill on separate line */}
                {timeUntilDue && (
                    <div className="px-4 pb-3 flex justify-center">
                        <span className={`text-[10px] text-white px-4 py-1.5 rounded-full ${timeUntilDue.color} font-bold whitespace-nowrap w-full max-w-[140px] text-center shadow-sm`}>
                            {timeUntilDue.text}
                        </span>
                    </div>
                )}

                {/* Staff - Editable with List view styling */}
                <div className="px-4 py-2.5 border-t border-slate-200/50">
                    {editingLocalField === workInfo.staffField ? (
                        <StaffDropdown
                            value={editingLocalValue}
                            options={staffList || []}
                            onChange={(value) => {
                                const fieldToUpdate = workInfo.staffField;
                                onUpdate(client.id, fieldToUpdate, value);
                                setTimeout(() => setEditingLocalField(null), 50);
                            }}
                            onCancel={cancelEdit}
                        />
                    ) : (
                        <div 
                            onClick={(e) => {
                                e.stopPropagation();
                                startEditing(workInfo.staffField, workInfo.assignedStaff);
                            }}
                            className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white/50 rounded p-1.5 -m-1.5 transition-colors"
                        >
                            <User className={`w-4 h-4 flex-shrink-0 ${assignedStaffName ? urgencyStyle.accentColor : 'text-slate-300'}`} />
                            <span className={`font-medium truncate flex-1 ${assignedStaffName ? 'text-slate-700' : 'text-slate-400'}`}>
                                {assignedStaffName || 'Unassigned'}
                            </span>
                            <Edit2 className="w-3 h-3 text-slate-300" />
                        </div>
                    )}
                </div>

                {/* Status - Editable on separate line */}
                <div className="px-4 py-2.5 border-t border-slate-200/50">
                    {editingLocalField === workInfo.statusField ? (
                        <StatusDropdown
                            value={editingLocalValue}
                            options={workflows[plannerType] || []}
                            onChange={(value) => {
                                const fieldToUpdate = workInfo.statusField;
                                onUpdate(client.id, fieldToUpdate, value);
                                setTimeout(() => setEditingLocalField(null), 50);
                            }}
                            onCancel={cancelEdit}
                        />
                    ) : (
                        <div 
                            onClick={(e) => {
                                e.stopPropagation();
                                startEditing(workInfo.statusField, workInfo.status);
                            }}
                            className="text-xs cursor-pointer hover:bg-white/50 rounded p-1.5 -m-1.5 flex items-center gap-2 transition-colors"
                        >
                            <span className="font-bold text-slate-600 uppercase tracking-wide text-[10px]">Status:</span>
                            <span className={`font-semibold flex-1 ${
                                workInfo.status?.toLowerCase().includes('filed') || workInfo.status?.toLowerCase().includes('completed') ? 'text-emerald-700' :
                                workInfo.status?.toLowerCase().includes('progress') ? 'text-blue-700' :
                                workInfo.status?.toLowerCase().includes('waiting') || workInfo.status?.toLowerCase().includes('requested') ? 'text-amber-700' :
                                'text-slate-400'
                            }`}>
                                {workInfo.status || 'Set Status'}
                            </span>
                            <Edit2 className="w-3 h-3 text-slate-300" />
                        </div>
                    )}
                </div>

                {/* Notes - Editable with proper background color and icon */}
                <div className="px-4 py-2.5 border-t border-slate-200/50">
                    {editingLocalField === 'notes' ? (
                        <textarea
                            value={editingLocalValue}
                            onChange={(e) => setEditingLocalValue(e.target.value)}
                            onBlur={saveEdit}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') cancelEdit();
                            }}
                            autoFocus
                            rows={3}
                            className="w-full text-xs bg-white border border-slate-200 rounded p-2 focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-700"
                            placeholder="Add notes..."
                        />
                    ) : (
                        <div 
                            onClick={(e) => {
                                e.stopPropagation();
                                startEditing('notes', client.notes);
                            }}
                            className="flex gap-2 p-2 rounded hover:bg-white/50 text-xs text-slate-600 italic cursor-pointer transition-colors min-h-[60px]"
                        >
                            <FileText className={`w-4 h-4 flex-shrink-0 mt-0.5 ${client.notes ? urgencyStyle.accentColor : 'text-slate-300'}`} />
                            <div className="flex-1 text-left">
                                {client.notes ? (
                                    <span className="text-slate-700 not-italic font-medium">{client.notes}</span>
                                ) : (
                                    <span className="text-slate-400">Click to add notes</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Primary Contact - Clickable */}
                {(client.pc_pname || client.pc_tel || client.pc_email) && (
                    <div className="px-4 pb-4 pt-3 border-t border-slate-200/50">
                        {/* Contact name and tel - tel aligned right */}
                        {(client.pc_pname || client.pc_tel) && (
                            <div 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClientClick && onClientClick(client);
                                }}
                                className="flex items-center justify-between gap-2 text-xs mb-1.5 cursor-pointer hover:bg-white/50 rounded p-1.5 -m-1.5 transition-colors"
                            >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <User className={`w-4 h-4 flex-shrink-0 ${urgencyStyle.accentColor}`} />
                                    <span className="text-slate-700 font-medium truncate">{client.pc_pname || '-'}</span>
                                </div>
                                {client.pc_tel && client.pc_tel !== '00000000000' && (
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <Phone className={`w-3 h-3 ${urgencyStyle.accentColor}`} />
                                        <span className={`font-semibold ${urgencyStyle.accentColor}`}>{client.pc_tel}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Email - aligned left */}
                        {client.pc_email && (
                            <div 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenEmail && onOpenEmail([{ email: client.pc_email, name: client.pc_pname || client.name }]);
                                }}
                                className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white/50 rounded p-1.5 -m-1.5 transition-colors"
                            >
                                <Mail className="w-4 h-4 flex-shrink-0 text-blue-500" />
                                <span className="text-blue-600 hover:underline truncate font-medium">{client.pc_email}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Month Column Component
    const MonthColumn = ({ month, clients }) => {
        // Filter clients based on assignment filter
        const filteredClients = assignmentFilter 
            ? clients.filter(c => {
                                const isAssigned = activeWorkstream?.staffField ? Boolean(c[activeWorkstream.staffField]) : false;
                return assignmentFilter === 'assigned' ? isAssigned : !isAssigned;
              })
            : clients;

        const stats = getCapacityStats(clients);
        const isOver = dragOverMonth === month;
        const monthLabel = month === 'Unscheduled' ? 'Unscheduled' :
            new Date(month + '-01').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
        const isUnscheduled = month === 'Unscheduled';

        return (
            <div
                onDragOver={(e) => handleDragOver(e, month)}
                onDrop={(e) => handleDrop(e, month)}
                className={`flex-shrink-0 w-72 bg-slate-50 rounded-xl border-2 flex flex-col transition-all ${
                    isOver ? 'border-sky-400 bg-sky-50 shadow-lg' : 'border-slate-200 shadow-sm'
                }`}
            >
                <div className={`p-4 border-b flex flex-col rounded-t-xl transition-all bg-gradient-to-b from-sky-400 to-sky-600 border-sky-700`}>
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                            <h3 className="text-base font-bold mb-1 transition-colors text-white">
                                {monthLabel}
                            </h3>
                            <div className="flex items-center gap-2">
                                <span className={`text-sm font-semibold px-2 py-1 rounded-md ${
                                    stats.totalJobs > 0 
                                        ? 'bg-white/20 text-white' 
                                        : 'bg-white/10 text-white/70'
                                }`}>
                                    {stats.totalJobs} {stats.totalJobs === 1 ? 'client' : 'clients'}
                                </span>
                                {stats.totalJobs > 0 && (
                                    <div className="flex items-center gap-2 text-xs">
                                        <button
                                            onClick={() => setAssignmentFilter(assignmentFilter === 'assigned' ? null : 'assigned')}
                                            className={`px-2 py-1 rounded-md transition-all cursor-pointer ${
                                                assignmentFilter === 'assigned'
                                                    ? 'bg-white/30 text-white font-semibold underline'
                                                    : 'text-white/80 hover:text-white hover:bg-white/10'
                                            }`}
                                        >
                                            {stats.assigned} assigned
                                        </button>
                                        <span className="text-white/60">•</span>
                                        <button
                                            onClick={() => setAssignmentFilter(assignmentFilter === 'unassigned' ? null : 'unassigned')}
                                            className={`px-2 py-1 rounded-md transition-all cursor-pointer ${
                                                assignmentFilter === 'unassigned'
                                                    ? 'bg-white/30 text-white font-semibold underline'
                                                    : 'text-white/80 hover:text-white hover:bg-white/10'
                                            }`}
                                        >
                                            {stats.unassigned} unassigned
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCapacityFor(showCapacityFor === month ? null : month)}
                            className={`p-2 rounded-lg transition-all ${
                                showCapacityFor === month
                                    ? 'bg-white/20 text-white'
                                    : 'hover:bg-white/10 text-white/70'
                            }`}
                            title="View capacity details"
                        >
                            <BarChart3 className="w-4 h-4" />
                        </button>
                    </div>

                    {showCapacityFor === month && (
                        <div className="bg-white/10 rounded-lg p-3 border border-white/20 animate-in fade-in zoom-in-95 duration-200">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-[11px]">
                                    <span className="font-medium text-white/90">Capacity Usage:</span>
                                    <div className="flex items-center gap-2 flex-1 ml-2">
                                        <div className="flex-1 bg-white/20 rounded-full h-2 overflow-hidden">
                                            <div 
                                                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                                                style={{ width: `${stats.capacity}%` }}
                                            />
                                        </div>
                                        <span className="font-bold w-10 text-right text-white">{stats.capacity}%</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200">
                                    <div className="text-[10px]">
                                        <span className="text-white/70">Assigned:</span>
                                        <div className="font-bold text-emerald-300">{stats.assigned}</div>
                                    </div>
                                    <div className="text-[10px]">
                                        <span className="text-white/70">Unassigned:</span>
                                        <div className="font-bold text-amber-300">{stats.unassigned}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedClients?.size > 0 && (
                        <button
                            onClick={() => onBulkMove && onBulkMove(month)}
                            className="w-full mt-3 text-xs font-bold py-2 rounded-lg transition-all shadow-sm bg-white/20 hover:bg-white/30 text-white hover:shadow-md"
                        >
                            Move {selectedClients.size} here
                        </button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {filteredClients.length === 0 ? (
                        <div className="text-center text-slate-400 text-sm italic py-8 font-medium">
                            {assignmentFilter ? `No ${assignmentFilter} clients` : 'No clients scheduled'}
                        </div>
                    ) : (
                        filteredClients.map(client => <ClientCard key={client.id} client={client} />)
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
            <div className="flex gap-4">
                <MonthColumn month="Unscheduled" clients={clientsByMonth['Unscheduled'] || []} />
                {planningMonths.map(month => (
                    <MonthColumn key={month} month={month} clients={clientsByMonth[month] || []} />
                ))}
            </div>
        </div>
    );
};

export default ClientKanbanView;